import * as React from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, Divider } from "@mui/material";
import { api } from "../../../Config/Api";
import { Order, OrderItem } from "../../../types/orderTypes";

function normalizeOneOrder(data: any): Order | null {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] ?? null;
  if (Array.isArray(data?.content)) return data.content[0] ?? null;
  if (Array.isArray(data?.orders)) return data.orders[0] ?? null;
  if (Array.isArray(data?.data)) return data.data[0] ?? null;
  return data as Order;
}

export default function SellerOrderView() {
  const { orderId } = useParams();
  const jwt = localStorage.getItem("jwt") || "";

  const [order, setOrder] = React.useState<Order | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>("");

  React.useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`/seller/orders/search?orderId=${orderId}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });

        const one = normalizeOneOrder(res.data);
        if (!one) {
          setError("Order not found");
          setOrder(null);
        } else {
          setOrder(one);
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load order");
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) fetchOrder();
  }, [orderId, jwt]);

  if (loading) {
    return (
      <Box className="p-5">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-5">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box className="p-5">
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-5">
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          Order #{order.id}
        </Typography>

        <Typography sx={{ mt: 1 }}>
          <strong>Status:</strong> {order.orderStatus}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={700}>
          Shipping Address
        </Typography>
        <Typography sx={{ mt: 1 }}>
          {order.shippingAddress?.name}
        </Typography>
        <Typography>
          {order.shippingAddress?.address}, {order.shippingAddress?.city}
        </Typography>
        <Typography>
          {order.shippingAddress?.state} - {order.shippingAddress?.pinCode}
        </Typography>
        <Typography>
          <strong>Mobile:</strong> {order.shippingAddress?.mobile}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={700}>
          Products
        </Typography>

        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {order.orderItems?.map((orderItem: OrderItem) => (
            <Paper key={orderItem.id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <img
                  src={orderItem.product?.images?.[0]}
                  alt=""
                  style={{ width: 90, borderRadius: 8 }}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography>
                    <strong>Title:</strong> {orderItem.product?.title}
                  </Typography>
                  <Typography>
                    <strong>Price:</strong> Rs.{orderItem.product?.sellingPrice}
                  </Typography>
                  <Typography>
                    <strong>Color:</strong> {orderItem.product?.color}
                  </Typography>
                  <Typography>
                    <strong>Size:</strong> {orderItem.size}
                  </Typography>
                  <Typography>
                    <strong>Qty:</strong> {orderItem.quantity}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}